import { createFileRoute } from "@tanstack/react-router";
import { useStore, getCashbook, store } from "@/lib/pos-store";
import { fmt } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printHtml, fmtNum as fmtNumPrint } from "@/lib/print-utils";

export const Route = createFileRoute("/reports/cashbook")({
  head: () => ({
    meta: [
      { title: "Cashbook Report" },
      {
        name: "description",
        content: "Daily cashbook with totals and balance carried forward.",
      },
    ],
  }),
  component: CashbookReport,
});

function CashbookReport() {
  const state = useStore((s) => s);
  const cb = getCashbook(state);

  const cols: { key: keyof typeof groups; label: string }[] = [
    { key: "purchase", label: "Purchases" },
    { key: "expenditure", label: "Expenditure" },
    { key: "debt", label: "Debts" },
    { key: "debt_paid", label: "Debts Paid" },
  ];
  const groups = {
    purchase: state.cash.filter((c) => c.type === "purchase"),
    expenditure: state.cash.filter((c) => c.type === "expenditure"),
    debt: state.cash.filter((c) => c.type === "debt"),
    debt_paid: state.cash.filter((c) => c.type === "debt_paid"),
  };
  const maxRows = Math.max(...cols.map((c) => groups[c.key].length), 1);

  const handlePrint = () => {
    // Build cash entries table
    let entriesHtml = "";
    for (let i = 0; i < maxRows; i++) {
      entriesHtml += "<tr>";
      for (const c of cols) {
        const e = groups[c.key][i];
        if (e) {
          entriesHtml += `<td><span class="desc">${e.description}</span> <span class="amount-cell" style="float:right;font-weight:bold;">${fmtNumPrint(e.amount)}</span></td>`;
        } else {
          entriesHtml += `<td>—</td>`;
        }
      }
      entriesHtml += "</tr>";
    }
    entriesHtml += `<tr class="total-row"><th class="num">Purchases</th><th class="num">Expenditure</th><th class="num">Debts</th><th class="num">Debts Paid</th></tr>`;
    entriesHtml += `<tr class="total-row">${cols.map((c) => `<td class="num">${fmtNumPrint(cb[c.key as keyof typeof cb] as number)}</td>`).join("")}</tr>`;

    const body = `
      <h1>Cashbook</h1>
      <div class="subtitle">${new Date().toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      <table>
        <thead><tr>${cols.map((c) => `<th>${c.label}</th>`).join("")}</tr></thead>
        <tbody>${entriesHtml}</tbody>
      </table>
      <div class="section-title">Summary</div>
      <div class="summary-line"><span>Previous Balance</span><span>${fmtNumPrint(cb.previousBalance)}</span></div>
      <div class="summary-line"><span>Total</span><span>${fmtNumPrint(cb.total)}</span></div>
      <div class="summary-line"><span>Purchases</span><span>${fmtNumPrint(cb.purchases)}</span></div>
      <div class="summary-line"><span>Expenditure</span><span>${fmtNumPrint(cb.expenditure)}</span></div>
      <div class="summary-line"><span>Banking</span><span>${fmtNumPrint(cb.banking)}</span></div>
      <div class="summary-line"><span>Debts</span><span>${fmtNumPrint(cb.debts)}</span></div>
      <div class="summary-line"><span>Short</span><span>${fmtNumPrint(cb.short)}</span></div>
      <div class="summary-line"><span>M-Pesa</span><span>${fmtNumPrint(cb.mpesa)}</span></div>
      <div class="summary-line"><span>Cash Handed Over</span><span>${fmtNumPrint(cb.cashHandedOver)}</span></div>
      <div class="summary-line total"><span>B.C.F (Balance Carried Forward)</span><span>${fmtNumPrint(cb.bcf)}</span></div>`;

    printHtml("Cashbook", body);
  };

  return (
    <div className="px-2 pt-3">
      <div className="mb-3 flex justify-end px-2">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="h-3.5 w-3.5" /> Print
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[640px] text-[11px]">
          <thead className="bg-secondary text-secondary-foreground">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className="px-2 py-2 text-left font-semibold">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {Array.from({ length: maxRows }).map((_, i) => (
              <tr key={i} className="bg-card align-top">
                {cols.map((c) => {
                  const e = groups[c.key][i];
                  return (
                    <td key={c.key} className="px-2 py-2">
                      {e ? (
                        <>
                          <div className="text-foreground">{e.description}</div>
                          <div className="font-semibold">{fmt(e.amount)}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-accent font-semibold text-accent-foreground">
              <td className="px-2 py-2">{fmt(cb.purchases)}</td>
              <td className="px-2 py-2">{fmt(cb.expenditure)}</td>
              <td className="px-2 py-2">{fmt(cb.debts)}</td>
              <td className="px-2 py-2">{fmt(cb.debtsPaid)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 space-y-2 rounded-xl border border-border bg-card p-4 text-sm">
        <Row
          label="Previous Balance"
          right={
            <Input
              type="number"
              className="h-7 w-28 text-right text-sm"
              value={state.meta.previousBalance}
              onChange={(e) => store.setPreviousBalance(+e.target.value || 0)}
            />
          }
        />
        <Row label="Total" value={fmt(cb.total)} bold />
        <Divider />
        <Row label="Purchases" value={fmt(cb.purchases)} />
        <Row label="Expenditure" value={fmt(cb.expenditure)} />
        <Row label="Banking" value={fmt(cb.banking)} />
        <Row label="Debts" value={fmt(cb.debts)} />
        <Row label="Short" value={fmt(cb.short)} />
        <Row label="M-Pesa" value={fmt(cb.mpesa)} />
        <Row label="Cash Handed Over" value={fmt(cb.cashHandedOver)} />
        <Divider />
        <Row
          label="B.C.F (Balance Carried Forward)"
          value={fmt(cb.bcf)}
          bold
          accent
        />
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  right,
  bold,
  accent,
}: {
  label: string;
  value?: string;
  right?: React.ReactNode;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`${bold ? "font-semibold" : ""} ${accent ? "text-primary" : ""}`}
      >
        {label}
      </span>
      {right ?? (
        <span
          className={`font-display ${bold ? "text-base font-bold" : ""} ${accent ? "text-primary" : ""}`}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function Divider() {
  return <div className="my-1 border-t border-dashed border-border" />;
}
